import { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  useEventTypes,
  useAvailability,
  useCreateReservation,
} from '../api/queries.js';
import { reservationPayloadSchema, computeReservationTotal } from '@waw/validation-schemas';
import AvailabilitySlot from '../components/AvailabilitySlot.jsx';
import './BookingTunnel.css';

// ─── Event type icons (SVG line, accent jaune) ──────────────────────────────
const ICON_PATHS = {
  // Gâteau
  anniversaire: (
    <>
      <path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8" />
      <path d="M4 16s.6-1 2-1 2.4 1 4 1 2.6-1 4-1 2.4 1 4 1 2-1 2-1" />
      <path d="M2 21h20" />
      <path d="M7 8v3M12 8v3M17 8v3" />
      <circle cx="7" cy="5" r=".5" /><circle cx="12" cy="5" r=".5" /><circle cx="17" cy="5" r=".5" />
    </>
  ),
  // Toque de diplômé
  sortie_scolaire: (
    <>
      <path d="M22 10 12 5 2 10l10 5 10-5Z" />
      <path d="M6 12v5c0 1 2.7 2 6 2s6-1 6-2v-5" />
    </>
  ),
  // Groupe de personnes
  team_building: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  // Boule + quille de bowling
  bowling: (
    <>
      <path d="M7.5 3.2c-1.2 0-1.9 1.3-1.9 2.8 0 .8.3 1.4.5 2 .2.6-.3 1.2-.4 1.9-.3 1.3-.2 2.8.2 3.9.3.9.9 1.5 1.6 1.5s1.3-.6 1.6-1.5c.4-1.1.5-2.6.2-3.9-.1-.7-.6-1.3-.4-1.9.2-.6.5-1.2.5-2 0-1.5-.7-2.8-1.9-2.8Z" />
      <path d="M6.3 9c.8.4 1.6.4 2.4 0" />
      <circle cx="16.5" cy="16" r="4.5" />
      <circle cx="15" cy="14.3" r=".7" /><circle cx="16.8" cy="13.9" r=".7" />
    </>
  ),
  // Micro (Karaoké)
  escape_game: (
    <>
      <rect x="9" y="2" width="6" height="11" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <path d="M12 17v4" />
      <path d="M8 21h8" />
    </>
  ),
  // Couronne
  privatisation: (
    <>
      <path d="M2 18h20" />
      <path d="m4 18-1.5-9 5 4 4.5-7 4.5 7 5-4L20 18" />
    </>
  ),
  // Étoiles
  autres: (
    <>
      <path d="M12 3l1.9 4.8L18.7 9l-4.8 1.9L12 15.7l-1.9-4.8L5.3 9l4.8-1.9z" />
      <path d="M19 14l.6 1.6 1.6.6-1.6.6-.6 1.6-.6-1.6-1.6-.6 1.6-.6z" />
    </>
  ),
};

function EventIcon({ type }) {
  return (
    <svg
      viewBox="0 0 24 24" width="30" height="30" fill="none"
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICON_PATHS[type] || ICON_PATHS.autres}
    </svg>
  );
}

const EVENT_LABELS = {
  anniversaire: 'Anniversaire',
  sortie_scolaire: 'Sortie scolaire',
  team_building: 'Team building',
  bowling: 'Bowling',
  escape_game: 'Karaoké',
  privatisation: 'Privatisation',
  autres: 'Autres',
};

const SITE_LABELS = {
  WAW_CASA: 'WAW Casablanca',
  WAW_WAVE: 'WAW Wave',
  WAW_FES: 'WAW Fès',
  WAW_MARRAKECH: 'WAW Marrakech',
  WAW_TANGER: 'WAW Tanger',
};

// ─── Calendar helpers ──────────────────────────────────────────────────────
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

function toDateStr(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function todayStr() {
  const t = new Date();
  return toDateStr(t.getFullYear(), t.getMonth(), t.getDate());
}

// ─── Customer form schema (subset for step 4) ─────────────────────────────
const customerFormSchema = z.object({
  name: z.string().trim().min(2, 'Nom requis').max(200),
  email: z.string().trim().toLowerCase().email('Email invalide'),
  phone: z.string().trim().min(1, 'Téléphone requis'),
  specialRequest: z.string().trim().max(2000).optional(),
});

// ─── Progress indicator ────────────────────────────────────────────────────
function Progress({ current, total }) {
  return (
    <div className="tunnel-progress" role="progressbar" aria-valuenow={current} aria-valuemax={total}>
      {Array.from({ length: total }).map((_, i) => {
        const step = i + 1;
        const isCompleted = step < current;
        const isActive = step === current;
        return (
          <div key={step} style={{ display: 'flex', alignItems: 'center' }}>
            {i > 0 && (
              <div className={`tunnel-step-line${isCompleted || isActive ? ' completed' : ''}`} />
            )}
            <div
              className={`tunnel-step-dot${isActive ? ' active' : ''}${isCompleted ? ' completed' : ''}`}
            >
              {isCompleted ? '✓' : step}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Calendar component ────────────────────────────────────────────────────
function Calendar({ selected, onSelect }) {
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());

  const today = todayStr();

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  // getDay() is 0=Sun, we need Mon=0
  const firstDayOfWeek = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7;

  const canGoPrev = viewYear > now.getFullYear() || (viewYear === now.getFullYear() && viewMonth > now.getMonth());

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  return (
    <div className="calendar">
      <div className="calendar-header">
        <button
          type="button"
          className="calendar-nav-btn"
          onClick={prevMonth}
          disabled={!canGoPrev}
          aria-label="Mois précédent"
        >
          &lsaquo;
        </button>
        <span className="calendar-month">
          {MONTHS[viewMonth]} {viewYear}
        </span>
        <button
          type="button"
          className="calendar-nav-btn"
          onClick={nextMonth}
          aria-label="Mois suivant"
        >
          &rsaquo;
        </button>
      </div>

      <div className="calendar-weekdays">
        {WEEKDAYS.map((d) => (
          <div key={d} className="calendar-weekday">{d}</div>
        ))}
      </div>

      <div className="calendar-days">
        {/* Empty cells before first day */}
        {Array.from({ length: firstDayOfWeek }).map((_, i) => (
          <div key={`e${i}`} className="calendar-day calendar-day-empty" />
        ))}

        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dateStr = toDateStr(viewYear, viewMonth, day);
          const isPast = dateStr < today;
          const isToday = dateStr === today;
          const isSelected = dateStr === selected;

          return (
            <button
              key={day}
              type="button"
              className={`calendar-day${isSelected ? ' selected' : ''}${isPast ? ' calendar-day-past' : ''}${isToday ? ' calendar-day-today' : ''}`}
              disabled={isPast}
              onClick={() => onSelect(dateStr)}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
export default function BookingTunnel() {
  const { siteCode } = useParams();
  const navigate = useNavigate();

  // ── State machine ──
  const [step, setStep] = useState(1);
  const [booking, setBooking] = useState({
    site: siteCode,
    eventType: '',
    eventTypeName: '',
    date: '',
    startTime: '',
    participants: 1,
    customer: { name: '', email: '', phone: '' },
    specialRequest: '',
  });
  const [result, setResult] = useState(null);
  const [toast, setToast] = useState('');

  // ── Queries ──
  const { data: eventTypes, isLoading: loadingTypes } = useEventTypes(siteCode);

  const availabilityParams = useMemo(() => {
    if (!booking.eventType || !booking.date) return null;
    return { site: siteCode, eventType: booking.eventType, date: booking.date };
  }, [siteCode, booking.eventType, booking.date]);

  const {
    data: availability,
    isLoading: loadingSlots,
    refetch: refetchSlots,
  } = useAvailability(availabilityParams);

  const createReservation = useCreateReservation();

  // ── Toast auto-dismiss ──
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  // ── Step 4 form ──
  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors: formErrors },
    setValue: setFormValue,
  } = useForm({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      name: booking.customer.name,
      email: booking.customer.email,
      phone: booking.customer.phone,
      specialRequest: booking.specialRequest,
    },
  });

  // ── Config (from event types) ──
  const selectedConfig = useMemo(() => {
    if (!eventTypes || !booking.eventType) return null;
    return eventTypes.find((et) => et.key === booking.eventType || et.code === booking.eventType);
  }, [eventTypes, booking.eventType]);

  // min/max/prix sont imbriqués sous .config dans la réponse API (jamais à la racine).
  const priceCfg = selectedConfig?.config || null;
  const maxParticipants = priceCfg?.maxParticipants || 50;
  const minParticipants = priceCfg?.minParticipants || 1;
  const pricePerPerson = priceCfg?.pricePerParticipant || null;
  const basePrice = priceCfg?.basePrice ?? null;
  const bookingTotal = computeReservationTotal(priceCfg, booking.participants);

  // ── Handlers ──
  const selectEventType = useCallback((type) => {
    const startCount = type.config?.minParticipants || 1;
    setBooking((b) => ({
      ...b,
      eventType: type.key || type.code,
      eventTypeName: type.label || type.name || EVENT_LABELS[type.key || type.code] || type.key,
      participants: startCount,
    }));
    setStep(2);
  }, []);

  const selectDate = useCallback((dateStr) => {
    setBooking((b) => ({ ...b, date: dateStr, startTime: '' }));
  }, []);

  const selectSlot = useCallback((time) => {
    setBooking((b) => ({ ...b, startTime: time }));
  }, []);

  const goToStep3 = useCallback(() => {
    if (booking.date && booking.startTime) setStep(3);
  }, [booking.date, booking.startTime]);

  const setParticipants = useCallback((delta) => {
    setBooking((b) => ({
      ...b,
      participants: Math.max(minParticipants, Math.min(maxParticipants, b.participants + delta)),
    }));
  }, [minParticipants, maxParticipants]);

  const onCustomerSubmit = useCallback((data) => {
    setBooking((b) => ({
      ...b,
      customer: { name: data.name, email: data.email, phone: data.phone },
      specialRequest: data.specialRequest || '',
    }));
    setStep(5);
  }, []);

  const goToStep = useCallback((s) => setStep(s), []);

  // ── Submit reservation ──
  const submitReservation = useCallback(async () => {
    setStep(6);
    try {
      const payload = {
        site: booking.site,
        eventType: booking.eventType,
        date: booking.date,
        startTime: booking.startTime,
        participants: booking.participants,
        customer: booking.customer,
        specialRequest: booking.specialRequest || undefined,
      };

      const res = await createReservation.mutateAsync(payload);
      setResult(res);

      if (res.paymentRedirectUrl) {
        // NAPS redirect
        window.location.href = res.paymentRedirectUrl;
        return;
      }

      setStep(7);
    } catch (err) {
      if (err.status === 409) {
        setToast('Ce créneau vient d\'être complété, merci de choisir un autre créneau.');
        refetchSlots();
        setBooking((b) => ({ ...b, startTime: '' }));
        setStep(2);
      } else if (err.status === 429) {
        setToast('Trop de tentatives, réessayez dans quelques instants.');
        setStep(5);
      } else {
        setToast(err.message || 'Une erreur est survenue. Veuillez réessayer.');
        setStep(5);
      }
    }
  }, [booking, createReservation, refetchSlots]);

  // ── Slots data ──
  const slots = availability?.slots || [];

  // ── Render steps ──
  const renderStep = () => {
    switch (step) {
      // ── Step 1: Event Type ──
      case 1:
        return (
          <div>
            <h2 className="tunnel-title">Choisissez votre activité</h2>
            <p className="tunnel-subtitle">Quel type d'événement souhaitez-vous organiser ?</p>

            {loadingTypes && (
              <div className="event-types-grid">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="skeleton" style={{ height: 120, borderRadius: 'var(--radius-client)' }} />
                ))}
              </div>
            )}

            {eventTypes && (
              <div className="event-types-grid">
                {eventTypes.map((type) => {
                  const key = type.key || type.code;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`event-type-card${booking.eventType === key ? ' selected' : ''}`}
                      onClick={() => selectEventType(type)}
                    >
                      <span className="event-type-icon">
                        <EventIcon type={key} />
                      </span>
                      <span className="event-type-name">
                        {type.label || type.name || EVENT_LABELS[key] || key}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );

      // ── Step 2: Date & Slots ──
      case 2:
        return (
          <div>
            <h2 className="tunnel-title">Date et créneau</h2>
            <p className="tunnel-subtitle">
              {booking.eventTypeName} &mdash; choisissez une date puis un créneau
            </p>

            <Calendar selected={booking.date} onSelect={selectDate} />

            {booking.date && (
              <div className="slots-section">
                <h3>Créneaux disponibles le {booking.date}</h3>

                {loadingSlots && (
                  <div className="slots-grid">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="skeleton" style={{ height: 52, borderRadius: 'var(--radius-client)' }} />
                    ))}
                  </div>
                )}

                {!loadingSlots && slots.length === 0 && (
                  <div className="slots-grid">
                    <p className="slots-empty">Aucun créneau disponible pour cette date.</p>
                  </div>
                )}

                {!loadingSlots && slots.length > 0 && (
                  <div className="slots-grid">
                    {slots.map((slot) => (
                      <AvailabilitySlot
                        key={slot.time}
                        time={slot.time}
                        available={slot.available}
                        remaining={slot.remaining}
                        disabled={slot.disabled || false}
                        selected={booking.startTime === slot.time}
                        onClick={selectSlot}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="tunnel-nav">
              <button type="button" className="btn btn-secondary" onClick={() => goToStep(1)}>
                Retour
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!booking.date || !booking.startTime}
                onClick={goToStep3}
              >
                Continuer
              </button>
            </div>
          </div>
        );

      // ── Step 3: Participants ──
      case 3:
        return (
          <div>
            <h2 className="tunnel-title">Nombre de participants</h2>
            <p className="tunnel-subtitle">
              {minParticipants > 1 ? `Minimum ${minParticipants}` : ''}{' '}
              {maxParticipants < 50 ? `Maximum ${maxParticipants}` : ''}
            </p>

            <div className="participants-stepper">
              <button
                type="button"
                className="stepper-btn"
                disabled={booking.participants <= minParticipants}
                onClick={() => setParticipants(-1)}
                aria-label="Moins"
              >
                &minus;
              </button>
              <span className="stepper-value">{booking.participants}</span>
              <button
                type="button"
                className="stepper-btn"
                disabled={booking.participants >= maxParticipants}
                onClick={() => setParticipants(1)}
                aria-label="Plus"
              >
                +
              </button>
            </div>

            {basePrice != null ? (
              <p className="stepper-price">
                {basePrice} MAD (jusqu&apos;à {priceCfg.baseParticipants} pers.)
                {priceCfg.extraParticipantPrice
                  ? ` + ${priceCfg.extraParticipantPrice} MAD/pers. sup.`
                  : ''}{' '}
                &mdash; <strong>{bookingTotal} MAD total</strong>
              </p>
            ) : pricePerPerson ? (
              <p className="stepper-price">
                {pricePerPerson} MAD / personne &mdash;{' '}
                <strong>{bookingTotal} MAD total</strong>
              </p>
            ) : null}

            <div className="tunnel-nav">
              <button type="button" className="btn btn-secondary" onClick={() => goToStep(2)}>
                Retour
              </button>
              <button type="button" className="btn btn-primary" onClick={() => goToStep(4)}>
                Continuer
              </button>
            </div>
          </div>
        );

      // ── Step 4: Coordinates ──
      case 4:
        return (
          <div>
            <h2 className="tunnel-title">Vos coordonnées</h2>
            <p className="tunnel-subtitle">Pour vous contacter concernant votre réservation</p>

            <form onSubmit={handleFormSubmit(onCustomerSubmit)} noValidate>
              <div className="form-group">
                <label className="form-label" htmlFor="book-name">Nom complet</label>
                <input
                  id="book-name"
                  type="text"
                  className={`form-input${formErrors.name ? ' error' : ''}`}
                  placeholder="Votre nom complet"
                  autoComplete="name"
                  {...register('name')}
                />
                {formErrors.name && <span className="form-error">{formErrors.name.message}</span>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="book-email">Email</label>
                <input
                  id="book-email"
                  type="email"
                  className={`form-input${formErrors.email ? ' error' : ''}`}
                  placeholder="votre@email.com"
                  autoComplete="email"
                  {...register('email')}
                />
                {formErrors.email && <span className="form-error">{formErrors.email.message}</span>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="book-phone">Téléphone</label>
                <input
                  id="book-phone"
                  type="tel"
                  className={`form-input${formErrors.phone ? ' error' : ''}`}
                  placeholder="+212 6XX XXX XXX"
                  autoComplete="tel"
                  {...register('phone')}
                />
                {formErrors.phone && <span className="form-error">{formErrors.phone.message}</span>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="book-special">Demande spéciale (optionnel)</label>
                <textarea
                  id="book-special"
                  className="form-input"
                  rows={3}
                  placeholder="Allergies, décoration, besoins particuliers..."
                  {...register('specialRequest')}
                />
              </div>

              <div className="tunnel-nav">
                <button type="button" className="btn btn-secondary" onClick={() => goToStep(3)}>
                  Retour
                </button>
                <button type="submit" className="btn btn-primary">
                  Continuer
                </button>
              </div>
            </form>
          </div>
        );

      // ── Step 5: Recap ──
      case 5: {
        const total = bookingTotal;
        return (
          <div>
            <h2 className="tunnel-title">Récapitulatif</h2>
            <p className="tunnel-subtitle">Vérifiez les détails avant de confirmer</p>

            <div className="card recap-card">
              <div className="recap-section">
                <span className="recap-label">Site</span>
                <span className="recap-value">{SITE_LABELS[siteCode] || siteCode}</span>
              </div>
              <div className="recap-section">
                <span className="recap-label">Activité</span>
                <span className="recap-value">
                  {booking.eventTypeName}
                  <button type="button" className="recap-edit" onClick={() => goToStep(1)}>Modifier</button>
                </span>
              </div>
              <div className="recap-section">
                <span className="recap-label">Date</span>
                <span className="recap-value">
                  {booking.date}
                  <button type="button" className="recap-edit" onClick={() => goToStep(2)}>Modifier</button>
                </span>
              </div>
              <div className="recap-section">
                <span className="recap-label">Créneau</span>
                <span className="recap-value">
                  {booking.startTime}
                  <button type="button" className="recap-edit" onClick={() => goToStep(2)}>Modifier</button>
                </span>
              </div>
              <div className="recap-section">
                <span className="recap-label">Participants</span>
                <span className="recap-value">
                  {booking.participants}
                  <button type="button" className="recap-edit" onClick={() => goToStep(3)}>Modifier</button>
                </span>
              </div>
              <div className="recap-section">
                <span className="recap-label">Contact</span>
                <span className="recap-value">
                  {booking.customer.name}<br />
                  {booking.customer.email}<br />
                  {booking.customer.phone}
                  <button type="button" className="recap-edit" onClick={() => goToStep(4)}>Modifier</button>
                </span>
              </div>
              {booking.specialRequest && (
                <div className="recap-section">
                  <span className="recap-label">Demande spéciale</span>
                  <span className="recap-value">{booking.specialRequest}</span>
                </div>
              )}

              {total && (
                <div className="recap-total">
                  <span className="recap-total-label">Total</span>
                  <span className="recap-total-value">{total} MAD</span>
                </div>
              )}
            </div>

            <div className="tunnel-nav">
              <button type="button" className="btn btn-secondary" onClick={() => goToStep(4)}>
                Retour
              </button>
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={submitReservation}
              >
                Confirmer la réservation
              </button>
            </div>
          </div>
        );
      }

      // ── Step 6: Submission loading ──
      case 6:
        return (
          <div className="submit-loading">
            <div className="submit-spinner" />
            <p style={{ color: 'var(--text-muted)' }}>Traitement de votre réservation...</p>
          </div>
        );

      // ── Step 7: Confirmation ──
      case 7:
        return (
          <div className="confirmation">
            <div className="confirmation-icon">{'✅'}</div>
            <h2 className="tunnel-title" style={{ textAlign: 'center' }}>
              Réservation enregistrée !
            </h2>
            <div className="confirmation-code">
              {result?.reservationCode || 'WAW-XXXXX'}
            </div>
            <p className="confirmation-msg">
              Vous recevrez un email de confirmation à l'adresse <strong>{booking.customer.email}</strong>.
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/" className="btn btn-secondary">
                Retour à l'accueil
              </Link>
              <Link
                to={`/reservation/${result?.reservationCode || ''}`}
                className="btn btn-primary"
              >
                Suivre ma réservation
              </Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <Progress current={step} total={7} />
      <div className="tunnel-container">
        {renderStep()}
      </div>
      {toast && <div className="tunnel-toast">{toast}</div>}
    </div>
  );
}
